import { Plugin } from '@ckeditor/ckeditor5-core';

export default class RocMediaEmbedTitle extends Plugin {
	static get pluginName() {
		return 'RocMediaEmbedTitle';
	}

	static get requires() {
		return ['MediaEmbedEditing'];
	}

	init() {
		const editor = this.editor;
		const conversion = editor.conversion;
		const elementName = editor.config.get('mediaEmbed.elementName');
		const renderMediaPreview = editor.config.get('mediaEmbed.previewsInData');
		const registry = editor.plugins.get('MediaEmbedEditing').registry;

		// Extend the model element to allows the `title` attribute.
		editor.model.schema.extend('media', { allowAttributes: ['title'] });
		patchRegistryGetMediaViewElement(registry);

		// View -> Model (data-oembed-url -> url)
		conversion.for('upcast').add((dispatcher) => {
			// Upcast semantic media.
			dispatcher.on(`element:${elementName}`, (evt, data, conversionApi) => {
				upcastTitleAttribute(data.viewItem, data, conversionApi);
			});

			// Upcast non-semantic media.
			dispatcher.on('element:div', (evt, data, conversionApi) => {
				const viewItem = data.viewItem;
				const isOembedUrl = viewItem.getAttribute('data-oembed-url');

				// Converting a non-oembed element.
				if (!isOembedUrl) {
					return;
				}

				upcastTitleAttribute(viewItem, data, conversionApi);
			});
		});

		// Model -> View.
		conversion.for('downcast').add((dispatcher) => {
			// How to convert the `title` attribute on model element in view.
			dispatcher.on('attribute:title:media', (evt, data, conversionApi) => {
				// The media will be already converted - so it will be present in the view.
				const viewFigure = conversionApi.mapper.toViewElement(data.item);
				const writer = conversionApi.writer;

				if (!viewFigure) {
					return;
				}

				if (data.attributeNewValue) {
					writer.setAttribute('oembed-title', data.attributeNewValue, viewFigure);
				} else {
					writer.removeAttribute('oembed-title', viewFigure);
				}
			});
		});

		// Keep stock converters and only re-render media content at low priority to add iframe title in RawElement callback.
		conversion.for('editingDowncast').add((dispatcher) => {
			registerMediaRerender(dispatcher, registry, (url) => ({
				elementName,
				renderForEditingView: true,
			}));
		});

		conversion.for('dataDowncast').add((dispatcher) => {
			registerMediaRerender(dispatcher, registry, (url) => ({
				elementName,
				renderMediaPreview: !!url && renderMediaPreview,
			}));
		});

		// Tries to convert the `oembed-title` attribute from view to the `title` attribute in model.
		//
		// @param {module:engine/view/element~Element} viewElement
		// @param {module:engine/conversion/upcastdispatcher~UpcastConversionData} data
		// @param {module:engine/conversion/upcastdispatcher~UpcastConversionApi} conversionApi
		function upcastTitleAttribute(viewElement, data, conversionApi) {
			const ownOembedTitle = viewElement.getAttribute('oembed-title');
			const parentElement =
				viewElement.parent && viewElement.parent.is && viewElement.parent.is('element')
					? viewElement.parent
					: null;
			const parentOembedTitle = parentElement ? parentElement.getAttribute('oembed-title') : null;
			const sourceElement = ownOembedTitle ? viewElement : parentOembedTitle ? parentElement : null;
			const oEmbedTitle = ownOembedTitle || parentOembedTitle;

			// The element does not contain the `oembed-title` attribute.
			if (!oEmbedTitle) {
				return;
			}

			const consumableAttributes = { attributes: ['oembed-title'] };

			// Check whether it can be consumed.
			// Might be consumed by something else - i.e. other converter with priority=highest - a standard check.
			if (!sourceElement || !conversionApi.consumable.consume(sourceElement, consumableAttributes)) {
				return;
			}

			const mediaElement = data.modelCursor.nodeBefore;

			if (!mediaElement || !mediaElement.is('element', 'media')) {
				return;
			}

			conversionApi.writer.setAttribute('title', oEmbedTitle, mediaElement);
		}
	}
}

/**
 * Re-render media content whenever media is inserted or its url/title changes.
 * Stock converters rebuild content for URL changes only; this ensures `mediaTitle`
 * is passed into getMediaViewElement() for both editing/data pipelines.
 */
function registerMediaRerender(dispatcher, registry, optionsFactory) {
	const rerender = (evt, data, conversionApi) => {
		const mediaItem = data.item;

		if (!mediaItem || !mediaItem.is || !mediaItem.is('element', 'media')) {
			return;
		}

		const viewFigure = conversionApi.mapper.toViewElement(mediaItem);

		if (!viewFigure) {
			return;
		}

		const writer = conversionApi.writer;
		const url = mediaItem.getAttribute('url');
		const mediaTitle = mediaItem.getAttribute('title');
		const mediaContentElement = Array.from(viewFigure.getChildren()).find(
			(child) => child.getCustomProperty && child.getCustomProperty('media-content'),
		);

		if (mediaContentElement) {
			writer.remove(mediaContentElement);
		}

		const mediaViewElement = registry.getMediaViewElement(writer, url, {
			...optionsFactory(url),
			mediaTitle,
		});
		writer.insert(writer.createPositionAt(viewFigure, 0), mediaViewElement);
	};

	dispatcher.on('insert:media', rerender, { priority: 'low' });
	dispatcher.on('attribute:url:media', rerender, { priority: 'low' });
	dispatcher.on('attribute:title:media', rerender, { priority: 'low' });
}

/**
 * Patch the registry once to inject iframe `title` inside RawElement render callback.
 * We intentionally fall back to the original method unless there is a mediaTitle and
 * preview rendering is active, preserving stock CKEditor behavior by default.
 */
function patchRegistryGetMediaViewElement(registry) {
	if (registry._rocIframeTitlePatched) {
		return;
	}

	const originalGetMediaViewElement = registry.getMediaViewElement.bind(registry);
	registry.getMediaViewElement = (writer, url, options = {}) => {
		const mediaTitle = options.mediaTitle;

		if (!mediaTitle) {
			return originalGetMediaViewElement(writer, url, options);
		}

		const media = registry._getMedia(url);

		if (!media) {
			return originalGetMediaViewElement(writer, url, options);
		}

		const shouldRenderPreview =
			options.renderForEditingView || (options.renderMediaPreview && media.url && media._previewRenderer);

		if (!shouldRenderPreview) {
			return originalGetMediaViewElement(writer, url, options);
		}

		const attributes = {};
		if (media.url) {
			attributes['data-oembed-url'] = media.url;
		}
		if (options.renderForEditingView) {
			attributes.class = 'ck-media__wrapper';
		}
		const mediaHtml = media._getPreviewHtml(options);
		const viewElement = writer.createRawElement('div', attributes, (domElement, domConverter) => {
			domConverter.setContentOf(domElement, mediaHtml);
			const iframe = domElement.querySelector('iframe');

			if (iframe) {
				iframe.setAttribute('title', mediaTitle);
			}
		});
		writer.setCustomProperty('media-content', true, viewElement);
		return viewElement;
	};

	registry._rocIframeTitlePatched = true;
}
